/**
 * ContractDurationSection — period-of-performance duration percentile shape
 * by vehicle category.
 *
 * Phase B3 migration: internals swapped from Recharts BarChart to
 * `BoxplotRenderer`. The mart returns p25/p50/p75/p90 day counts per
 * (FY, vehicle, pricing); we aggregate to one row per vehicle category and
 * pass the duration_days percentile fields explicitly.
 */
import { useMemo } from 'react'
import { govdataAPI } from '../../services/govdata'
import { useGovdata } from './useGovdata'
import SectionCard from './SectionCard'
import BoxplotRenderer from '../exec/generic/BoxplotRenderer'
import type { DashboardFilters } from '../../types/govdata'
import type { PerfRow } from '../exec/generic/types'

interface Props {
  filters: DashboardFilters
  embedded?: boolean
}

const DURATION_FIELDS: [string, string, string, string, string] = [
  'p25_duration_days',
  'p25_duration_days',
  'p50_duration_days',
  'p75_duration_days',
  'p90_duration_days',
]

export default function ContractDurationSection({ filters, embedded = false }: Props) {
  const paramsKey = JSON.stringify(filters)
  const { data, loading, error } = useGovdata(
    () => govdataAPI.contractDuration(filters),
    paramsKey,
  )

  const rows: PerfRow[] = useMemo(() => {
    // Aggregate by vehicle category, weighting percentiles by award count.
    type Acc = { n: number; p25: number; p50: number; p75: number; p90: number }
    const byVehicle = new Map<string, Acc>()
    for (const r of data) {
      const vehicle = r.vehicle_category || 'Other'
      const n = r.award_count || 0
      if (n === 0) continue
      const e = byVehicle.get(vehicle) || { n: 0, p25: 0, p50: 0, p75: 0, p90: 0 }
      e.n += n
      e.p25 += (r.p25_duration_days || 0) * n
      e.p50 += (r.p50_duration_days || 0) * n
      e.p75 += (r.p75_duration_days || 0) * n
      e.p90 += (r.p90_duration_days || 0) * n
      byVehicle.set(vehicle, e)
    }
    const out: PerfRow[] = Array.from(byVehicle.entries()).map(([vehicle, e]) => ({
      vehicle_category: vehicle,
      vehicle_category_label: vehicle.length > 22 ? vehicle.slice(0, 20) + '…' : vehicle,
      p25_duration_days: e.n > 0 ? Math.round(e.p25 / e.n) : 0,
      p50_duration_days: e.n > 0 ? Math.round(e.p50 / e.n) : 0,
      p75_duration_days: e.n > 0 ? Math.round(e.p75 / e.n) : 0,
      p90_duration_days: e.n > 0 ? Math.round(e.p90 / e.n) : 0,
      award_count: e.n,
    }))
    return out.sort((a, b) => Number(b.p50_duration_days) - Number(a.p50_duration_days)).slice(0, 10)
  }, [data])

  return (
    <SectionCard
      title="Contract Duration"
      subtitle="Period of performance percentiles by vehicle category — top 10 by median"
      loading={loading}
      error={error}
      embedded={embedded}
    >
      <div className={embedded ? 'h-full w-full' : 'h-72'}>
        <BoxplotRenderer
          data={rows}
          groupBy={'vehicle_category' as never}
          percentileFields={DURATION_FIELDS}
        />
      </div>
    </SectionCard>
  )
}
